import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from '../../../context/NotificationContext';
import { Card, Table, Input, Button, Typography, Tag } from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { useCommon } from "../../../context/CommonContext";

const { Title, Text } = Typography;
const { Search } = Input;

const TermsConditionsList = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();

  // State management
  const [loading, setLoading] = useState(false);
  const [termsConditions, setTermsConditions] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} of ${total} terms & conditions`,
  });

  // Filters
  const [filters, setFilters] = useState({
    searchText: "",
  });

  const getTermsConditions = useCallback(
    async (params = {}) => {
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.searchText,
          ...params,
        };

        console.log("Fetching terms & conditions with params:", requestParams);
        const response = await masterService.getTermsConditions(requestParams);
        console.log("Terms & Conditions API Response:", response);

        if (response.data && response.data.status) {
          // Handle different possible response structures
          const termsConditionsData =
            response.data.data?.data || response.data.data || [];
          const paginationData =
            response.data.data?.pagination || response.data.pagination || {};

          console.log("Terms & Conditions data:", termsConditionsData);
          console.log("Pagination data:", paginationData);

          // Ensure termsConditionsData is an array
          if (Array.isArray(termsConditionsData)) {
            setTermsConditions(termsConditionsData);
          } else {
            console.error(
              "Terms & Conditions data is not an array:",
              termsConditionsData
            );
            setTermsConditions([]);
          }

          setPagination((prev) => ({
            ...prev,
            ...paginationData,
            current: paginationData.currentPage || 1,
          }));
        } else {
          notify.error({ message: "Failed to fetch terms & conditions" });
          setTermsConditions([]);
        }
      } catch (error) {
        console.error("Error fetching terms & conditions:", error);
        notify.error({ message: "Failed to fetch terms & conditions" });
        setTermsConditions([]);
      } finally {
        setLoading(false);
      }
    },
    [filters.searchText]
  );

  // Load terms & conditions data
  useEffect(() => {
    setPageTitle("Terms & Conditions");
    getTermsConditions();
  }, [getTermsConditions, setPageTitle]);

  // Handle search
  const handleSearch = (value) => {
    setFilters((prev) => ({ ...prev, searchText: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getTermsConditions({
      searchText: value,
      perPage: pagination.pageSize,
      currentPage: 1,
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;

    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));

    getTermsConditions({
      searchText: filters.searchText,
      perPage: pageSize,
      currentPage: current,
    });
  };

  // Handle add terms & conditions
  const handleAddTermsConditions = () => {
    navigate("/terms-conditions/add/new");
  };

  // Handle edit terms & conditions
  const handleEditTermsConditions = (tcId) => {
    navigate(`/terms-conditions/edit/${tcId}`);
  };

  // Table columns
  const columns = [
    {
      title: "Order",
      dataIndex: "tc_order",
      key: "tc_order",
      width: 80,
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.tc_order - b.tc_order,
      render: (text) => <Text style={{ fontSize: "14px" }}>{text}</Text>,
    },
    {
      title: "Heading",
      dataIndex: "tc_heading",
      key: "tc_heading",
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 120,
      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditTermsConditions(record.tc_id)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  // Header content with user info

  return (
      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Terms & Conditions ({pagination.total})
          </Title>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              minWidth: 300,
              flex: 1,
              justifyContent: "flex-end",
            }}
          >
            <Search
              placeholder="Search terms & conditions..."
              allowClear
              onSearch={handleSearch}
              style={{ width: 320, maxWidth: "40vw" }}
              enterButton={<SearchOutlined />}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddTermsConditions}
              size="middle"
            >
              Add Terms & Conditions
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={termsConditions}
          rowKey="tc_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
          locale={{
            emptyText: "No terms & conditions found",
            filterConfirm: "OK",
            filterReset: "Reset",
            filterEmptyText: "No filters",
          }}
        />
      </Card>
  );
};

export default TermsConditionsList;
